import {
  disconnectDynamicPage,
  deleteDynamicPage,
  updateDynamicPageUrl,
  updateDynamicPageSEO,
  duplicateDynamicPage,
  getDpCreationFlows,
  addBlankDynamicPage,
  addCategoryPage,
} from '../business-logic/dynamic-pages/dynamicPagesApi'
import { createDPErrorHandler } from '../worker/utils/createDPErrorHandler'
import createNewDynamicPageV4PanelOpener from '../panels/new-dynamic-page-panel-v4/getPanelOpener'
import { openPanel as openNewDynamicPagePanel } from '../panels/new-dynamic-page-panel-v5/openPanel'
import { openPanel as openNewDynamicPageIntroPanel } from '../panels/new-dynamic-page-intro-panel/openPanel'
import { openPanel as openDynamicEditPagePanel } from '../panels/dynamic-edit-page-panel/openPanel'
import { openEditDynamicPageUrlStructurePanel } from '../panels/edit-dynamic-page-url-structure-panel/openPanel'
import {
  getAssetUrlInWorker,
  isSinglesCollectionSchema,
  getDeveloperMode,
  getPublishingPluginPatches,
  isBlocks,
  syncRouters,
  serializeFilter,
  DYNAMIC_PAGE_VARIANT,
  setDynamicPageTypeStateForPage,
  PAGE_TYPES,
  getOriginalPageRefForVariant,
} from '@wix/wix-data-client-common'
import {
  generateCollectionId,
  DEFAULT_PUBLISHING_MODE,
} from '@wix/wix-data-client-common-standalone'
import { openPanelByController as openDynamicPageInfoPanel } from '../panels/page-info-panel/openPanel'
import openPresetScanningPanel from '../panels/preset-scanning-panel/openPanel'
import { defaultPanelBiParams } from './defaultBiParams'
import { addPreset } from './addPreset'
import { openDPsFromSinglesErrorPanel } from './openDPsFromSinglesErrorPanel'
import openConvertToDynamicPagePanelV2 from '../panels/convert-to-dynamic-page-panel-v2/openPanel'
import openFieldPropertiesPanel from '../panels/field-properties-panel/openPanel'
import openRouterHooksPanel from '../panels/router-hooks-panel/openPanel'
import { getDynamicPageType } from '../business-logic/dynamic-pages/getDynamicPageType'

import { setUpDynamicPageUrlCounter } from './dynamic-page-url-counter'
import { notifyIfInsufficientEditingPermissionsForMembers } from './notifyIfInsufficientEditingPermissionsForMembers'
import { refreshRoutersAndEmitPageLinkGenerationEvent } from './utils/refreshRoutersAndEmitPageLinkGenerationEvent'
import { saveInEditDynamicPageUrlStructurePanel } from './save-in-edit-dynamic-page-url-structure-panel'
import { fetchRouterDatasetFromPage } from '../business-logic/dynamic-pages/fetchRouterDatasetFromPage'
import { isDuplicateRouterPrefix } from '../business-logic/dynamic-pages/generateUniquePrefix'
import { openDeletePagePanel } from './delete-dynamic-page/openDeletePagePanel'
import { getEditor3Api } from './apiEditor3'

import type { HttpClient } from '@wix/http-client'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type Experiments from '@wix/wix-experiments'
import type {
  AppApi,
  DpBiDefaults,
  DpPageType,
  DynamicPagesAppApi,
  Features,
  EDITOR_TYPE,
  WixDataClient,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  Logger,
  I18n,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import { addRouterDataset } from '../business-logic/dynamic-pages/add-dynamic-pages/new-page/addRouterDataset'
import type { PageRef } from '@wix/editor-platform-sdk-types'

type Params = {
  fedopsLogger: FedopsLogger
  i18n: I18n
  logger: Logger
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  wixData: WixDataClient
  editorType: EDITOR_TYPE
  hostname: string
  finalAppApiPromise: Promise<AppApi>
  experimentsManager: Experiments
  httpClient: HttpClient
  features: Features
}

export const apiCreator = ({
  fedopsLogger,
  i18n,
  logger,
  editorSdkProxy,
  collectionsApi,
  wixData,
  editorType,
  hostname,
  finalAppApiPromise,
  experimentsManager,
  httpClient,
  features,
}: Params): DynamicPagesAppApi => {
  const dynamicPageUrlCounter = setUpDynamicPageUrlCounter({
    editorSdkProxy,
    httpClient,
    finalAppApiPromise,
    editorType,
  })

  const getDpFlows = async ({ biDefaults }: { biDefaults: DpBiDefaults }) => {
    const appApi = await finalAppApiPromise
    const { sandboxEnabled: isSandboxEnabled } = await appApi.getDataSettings()

    const defaultDraftPublishStatus = DEFAULT_PUBLISHING_MODE
    const isDraftPublishActivatedExperimentEnabled = experimentsManager.enabled(
      'specs.wixCode.DraftPublishActivatedByDefaultV2',
    )

    const isDeveloperModeEnabled = await getDeveloperMode(editorSdkProxy)

    const withDraftPublishActivatedByDefault =
      isDraftPublishActivatedExperimentEnabled &&
      !isBlocks(editorType) &&
      !isDeveloperModeEnabled

    const pluginsPatches = withDraftPublishActivatedByDefault
      ? getPublishingPluginPatches(defaultDraftPublishStatus)
      : []

    return getDpCreationFlows({
      collectionsApi,
      editorSdkProxy,
      wixData,
      i18n,
      editorType,
      isSandboxEnabled,
      defaultDraftPublishStatus,
      pluginsPatches,
      logger,
      fedopsLogger,
      appApi,
      biDefaults,
      experimentsManager,
      httpClient,
      features,
    })
  }

  const withDPErrorHandler = <T>(fn: () => Promise<T>) =>
    createDPErrorHandler<T>({
      i18n,
      logger,
      editorSdkProxy,
    })(fn)

  const addDefaultPage =
    ({ pageKey }: { pageKey: DpPageType }) =>
    async ({
      collectionId,
      origin,
      panelName = defaultPanelBiParams.panel_name,
    }: {
      collectionId: string
      origin: string
      panelName?: string
    }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const appApi = await finalAppApiPromise

        return appApi.addDynamicPages({
          presetId: 'blank',
          pagesToAdd: [{ key: pageKey }],
          collectionId,
          collectionName: schema.displayName,
          fromExistingCollection: true,
          biDefaults: {
            is_first_data_modal: false,
            panel_name: panelName,
            origin,
          },
        })
      })

  return {
    async saveNewDynamicPageUrl({ pageRef, collectionId, newUrl }) {
      return updateDynamicPageUrl({
        editorSdkProxy,
        collectionsApi,
        pageRef,
        collectionId,
        newUrl,
      })
    },

    async updateDynamicPageSeo({ pageRef, title, metatags }) {
      return updateDynamicPageSEO({
        editorSdkProxy,
        pageRef,
        title,
        metatags,
      })
    },

    addDynamicPages: async ({
      pagesToAdd,
      collectionId,
      collectionName,
      biDefaults,
      presetId = 'blank',
      isIndexable = true,
      fromExistingCollection = false,
    }) => {
      const dpFlows = await getDpFlows({ biDefaults })

      const params = {
        presetId,
        isIndexable,
        pagesToAdd,
        collectionId,
        collectionName,
        origin: biDefaults.origin,
      }

      return fromExistingCollection
        ? dpFlows.addDefaultDynamicPages(params)
        : dpFlows.addPreset(params)
    },

    addBlankDynamicPage: async ({ collectionId, origin, panelName }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const biDefaults = {
          origin,
          panel_name: panelName,
          is_first_data_modal: false,
        }

        return addBlankDynamicPage({
          editorSdkProxy,
          collectionsApi,
          appApi: await finalAppApiPromise,
          experimentsManager,
          i18n,
          logger,
          fedopsLogger,
          editorType,
          collectionId,
          biDefaults,
        })
      }),

    addDefaultDynamicItemPage: addDefaultPage({
      pageKey: 'itemPage',
    }),

    addDefaultDynamicListPage: addDefaultPage({
      pageKey: 'indexPage',
    }),

    convertToDynamicAndBindComponents: async ({
      pageRef,
      collectionId,
      collectionName,
      fields = {},
      records = [],
      bindings = [],
      biDefaults,
    }) =>
      withDPErrorHandler(async () => {
        const dpFlows = await getDpFlows({
          biDefaults: { ...biDefaults, is_first_data_modal: false },
        })

        return dpFlows.convertFromScratchAndBindComponents({
          pageRef,
          collectionId,
          collectionName,
          fields,
          records,
          bindings,
          origin: biDefaults.origin,
        })
      }),

    convertStaticToDynamicFromCollection: async ({
      pageRef,
      collectionId,
      biDefaults,
    }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const dpFlows = await getDpFlows({
          biDefaults: { ...biDefaults, is_first_data_modal: false },
        })

        return dpFlows.convertFromCollection({
          pagesToAdd: [{ key: 'itemPage', staticPageRef: pageRef }],
          collectionId,
          collectionName: schema.displayName,
          origin: biDefaults.origin,
        })
      }),

    addPresetWithGivenPages: async ({
      presetId,
      pagesToAdd,
      collectionName,
      origin,
      isFirstTimeExperience = false,
    }) =>
      withDPErrorHandler(async () => {
        const allCollections = await collectionsApi.getAll()

        const collectionId = generateCollectionId(
          collectionName,
          allCollections,
        )

        const appApi = await finalAppApiPromise
        return appApi.addDynamicPages({
          presetId,
          pagesToAdd,
          collectionId,
          collectionName,
          biDefaults: {
            origin,
            is_first_data_modal: isFirstTimeExperience,
          },
        })
      }),

    addPresetUnhandled: async ({ presetId, origin, isFirstTimeExperience }) =>
      addPreset({
        appApi: await finalAppApiPromise,
        collectionsApi,
        editorType,
        presetId,
        origin,
        isFirstTimeExperience,
        editorSdkProxy,
      }),

    addPreset: async params =>
      withDPErrorHandler(async () =>
        (await finalAppApiPromise).addPresetUnhandled(params),
      ),

    async disconnectDynamicPage({ routerRef, pageRef }) {
      try {
        return await disconnectDynamicPage({
          editorSdkProxy,
          collectionsApi,
          routerRef,
          pageRef,
          editorType,
          appApi: await finalAppApiPromise,
        })
      } catch (error) {
        const wrappedError = new Error(`disconnectDynamicPage failed`, {
          cause: error,
        })
        logger.error(wrappedError)
        throw wrappedError
      }
    },

    async deleteDynamicPage({ pageRef }) {
      try {
        fedopsLogger.interactionStarted('deleteDynamicPage', {
          interactionTimeout: 6000,
        })

        await deleteDynamicPage({
          editorSdkProxy,
          collectionsApi,
          pageRef,
          editorType,
          appApi: await finalAppApiPromise,
        })

        fedopsLogger.interactionEnded('deleteDynamicPage')
      } catch (error) {
        throw new Error(`deleteDynamicPage failed`, { cause: error })
      }
    },

    openDeleteDynamicPagePanel: async ({ pageRef, origin }) =>
      openDeletePagePanel(
        {
          editorSdkProxy,
          logger,
          appApi: await finalAppApiPromise,
          collectionsApi,
          i18n,
          editorType,
        },
        { pageRef, origin },
      ),

    openDeleteDynamicPageConfirmationPanel: ({ pageTitle }) =>
      editorSdkProxy.editor.openErrorPanel({
        helpId: '35b6fd54-22ae-4251-aa8d-2db717085ea3',
        headerText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Title',
        ),
        topDescriptionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Text',
          { nameOfPage: pageTitle },
        ),
        mainActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button2',
        ),
        secondaryActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button1',
        ),
        shouldShowIllustration: false,
      }),

    openDisconnectDynamicPageConfirmationPanel: async () =>
      editorSdkProxy.editor.openErrorPanel({
        helpId: 'e1add2cd-b7d4-45fa-b20f-57a3ad2c4609',
        headerText: i18n.t(
          'Workspace_DYNAMIC_PAGES_WARNING_REMOVE_DATASET_TITLE',
        ),
        topDescriptionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Paragraph1',
        ),
        mainActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button2',
        ),
        secondaryActionText: i18n.t(
          'Workspace_Dynamic_Pages_Warning_Remove_Dataset_Button1',
        ),
        illustration: await getAssetUrlInWorker(
          editorSdkProxy,
          'panel-icons/disconnect_dynamic_page.svg',
        ),
        shouldShowIllustration: false,
      }),

    async openAddDynamicPagePanelV4({ origin, isFirstTimeExperience = false }) {
      const openNewDynamicPagePanel = createNewDynamicPageV4PanelOpener({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        i18n,
        editorType,
        isFirstTimeExperience,
      })
      return openNewDynamicPagePanel({
        origin,
      })
    },

    async openAddDynamicPagePanelV5({
      origin,
      collectionId,
      isFirstTimeExperience = false,
      canChangeCollection = true,
    }) {
      return openNewDynamicPagePanel({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        editorType,
        collectionId,
        isFirstTimeExperience,
        origin,
        canChangeCollection,
        experimentsManager,
      })
    },

    async openNewDynamicPageIntroPanel({ origin }) {
      return openNewDynamicPageIntroPanel({
        appApi: await finalAppApiPromise,
        hostname,
        editorSdkProxy,
        editorType,
        origin,
      })
    },

    async openDynamicPageInfoPanel({ controllerRef }) {
      return openDynamicPageInfoPanel({ editorSdkProxy }, { controllerRef })
    },

    async openDynamicPageSEOPanel({ pageRef }) {
      return editorSdkProxy.editor.openPagesPanel({
        pageRef,
        // Note for future conversion to TS: Platform documentation demands tab type "SEO", but API works correctly with "seo"
        initialSettingsTabType: 'seo',
      })
    },

    async openPresetScanningPanel({ pageRef }) {
      return openPresetScanningPanel({
        hostname,
        editorSdkProxy,
        editorType,
        pageRef,
      })
    },

    duplicateDynamicPage: async ({ pageRef, origin }) =>
      withDPErrorHandler(() =>
        duplicateDynamicPage({
          pageRef,
          origin,
          editorSdkProxy,
          httpClient,
          collectionsApi,
          logger,
          i18n,
          editorType,
          fedopsLogger,
          experimentsManager,
          appApiPromise: finalAppApiPromise,
        }),
      ),

    openConvertToDynamicPagePanelV2: async ({ pageRef, origin }) =>
      openConvertToDynamicPagePanelV2({
        editorSdkProxy,
        appApi: await finalAppApiPromise,
        hostname,
        initialData: { pageRef, origin, editorType },
      }),

    openFieldPropertiesPanel: async ({
      name,
      keyIsRequired,
      key,
      typeOptions,
      type,
      otherKeys,
      origin,
      y,
    }) =>
      openFieldPropertiesPanel({
        editorSdkProxy,
        hostname,
        i18n,
        initialData: {
          name,
          keyIsRequired,
          key,
          typeOptions,
          type,
          otherKeys,
          origin,
          editorType,
        },
        y,
      }),

    openRouterHooksPanel: async ({ routerRef }) =>
      openRouterHooksPanel(
        {
          editorSdkProxy,
          hostname,
          i18n,
        },
        {
          routerRef,
        },
      ),

    getDynamicPageType: async ({ pageRef }) =>
      getDynamicPageType({ editorSdkProxy, pageRef }),

    getDynamicPageUrlCount: async ({ pageRef }) =>
      dynamicPageUrlCounter?.getCount(pageRef) ?? null,

    getAllDynamicPageUrlCounts: async () =>
      dynamicPageUrlCounter?.getAllCounts() ?? {},

    refreshDynamicPageUrlCounts: async pageRefs =>
      dynamicPageUrlCounter?.refreshCounts(pageRefs),

    addCategoryPage: async ({ collectionId, origin }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        return addCategoryPage({
          editorSdkProxy,
          collectionsApi,
          appApi: await finalAppApiPromise,
          experimentsManager,
          i18n,
          logger,
          fedopsLogger,
          editorType,
          collectionId,
          biDefaults: {
            origin,
            is_first_data_modal: false,
          },
        })
      }),

    addDynamicEditPage: async ({ collectionId, origin, panelName }) =>
      withDPErrorHandler(async () => {
        const schema = await collectionsApi.get(collectionId)

        if (isSinglesCollectionSchema(schema)) {
          return openDPsFromSinglesErrorPanel({
            editorSdkProxy,
            logger,
            i18n,
          })
        }

        const appApi = await finalAppApiPromise

        const result = await appApi.addDynamicPages({
          pagesToAdd: [{ key: 'itemPage' }],
          collectionId,
          collectionName: schema.displayName,
          biDefaults: {
            origin,
            panel_name: panelName,
            is_first_data_modal: false,
          },
          fromExistingCollection: true,
          presetId: 'edit',
          isIndexable: false,
        })

        notifyIfInsufficientEditingPermissionsForMembers({
          appApi,
          builderType: 'studio',
          collectionId,
          collectionName: schema.displayName, // Currently, this type of page is only supported for collections without a namespace.
          i18n,
          logger,
          origin: 'edit_page',
          pageType: 'edit_page',
          permissions: schema.permissions,
        })

        return result
      }),

    refreshRoutersAndEmitPageLinkGenerationEvent: async ({
      collectionId,
      pageLinkKeys,
    }) => {
      const appApi = await finalAppApiPromise
      return refreshRoutersAndEmitPageLinkGenerationEvent({
        collectionsApi,
        editorSdkProxy,
        appApi,
        collectionId,
        pageLinkKeys,
      })
    },

    async openDynamicEditPagePanel({
      collectionId,
      isFirstTimeExperience = false,
      origin,
    }) {
      let schema
      try {
        schema = await collectionsApi.get(collectionId)
      } catch (error) {
        logger.error(
          new Error(`Failed to get collection schema`, { cause: error }),
        )
        schema = null
      }

      return openDynamicEditPagePanel({
        appApi: await finalAppApiPromise,
        collectionId,
        collectionName: schema?.displayName,
        permissions: schema?.permissions,
        editorSdkProxy,
        editorType,
        hostname,
        isFirstTimeExperience,
        origin,
      })
    },

    async openEditDynamicPageUrlStructurePanel({
      pageName,
      pageRef,
      digestedSchema,
      initialUrl,
      config,
      biData,
    }) {
      return await openEditDynamicPageUrlStructurePanel({
        editorSdkProxy,
        hostname,
        initialData: {
          pageName,
          pageRef,
          digestedSchema,
          initialUrl,
          config,
          biData,
        },
      })
    },

    async saveInEditDynamicPageUrlStructurePanel({
      pageRef,
      collectionId,
      newUrl,
    }) {
      return saveInEditDynamicPageUrlStructurePanel({
        appApi: await finalAppApiPromise,
        editorSdkProxy,
        i18n,
        logger,
        httpClient,
        pageRef,
        collectionId,
        newUrl,
        collectionsApi,
      })
    },

    fetchRouterDatasetFromPage: async pageRef => {
      const routerDataset = await fetchRouterDatasetFromPage(
        editorSdkProxy,
        pageRef,
      )
      return routerDataset?.ref || null
    },

    async isDuplicateRouterPrefix({ prefix }) {
      return await isDuplicateRouterPrefix({
        editorSdkProxy,
        prefix,
        ignorePageType: true,
      })
    },

    async handleChangedRouters({ pageRefs }) {
      const [appApi, routers] = await Promise.all([
        finalAppApiPromise,
        editorSdkProxy.routers.getAll<RouterConfig>(),
      ])

      const syncAndRefresh = async () => {
        try {
          await syncRouters({
            editorSdkProxy,
            httpClient,
            routers,
          })
          await appApi.refreshSchemasCache()
        } catch (cause) {
          logger.error(
            new Error(
              'failed to syncRouters and refreshSchemasCache in handleChangedRouters',
              { cause },
            ),
            { routers },
          )
        }
      }
      syncAndRefresh()

      editorSdkProxy.editor.routers.refresh()
      appApi.emit('routersChanged', { routers, pageRefs })
    },

    addDpVariant: async payload => {
      const { type: layout, title, filters, pageRef, routerRef } = payload
      const appApi = await finalAppApiPromise

      const { config } = await editorSdkProxy.routers.get<RouterConfig>({
        routerRef,
      })
      await editorSdkProxy.routers.update({
        routerRef,
        config: {
          ...config,
          variantRoutesStrategy: 'own',
        },
      })

      const variantPageRef = await createDpVariant({
        layout,
        title,
        filters,
        originalPageRef: pageRef,
        editorSdkProxy,
        appApi,
        editorType,
        pageIdForDuplication: pageRef.id,
      })

      return { variantPageRef }
    },

    duplicateDpVariant: async payload => {
      const { type: layout, title, filters, variantPageRef } = payload
      const appApi = await finalAppApiPromise

      const originalPageRef = await getOriginalPageRefForVariant({
        variantPageRef,
        editorSdkProxy,
      })

      const newVariantPageRef = await createDpVariant({
        layout,
        title,
        filters,
        originalPageRef,
        editorSdkProxy,
        appApi,
        editorType,
        pageIdForDuplication: variantPageRef.id,
      })

      return { variantPageRef: newVariantPageRef }
    },

    updateDpVariant: async payload => {
      const { filters, pageRef } = payload

      const currentRule =
        await editorSdkProxy.editor.routers.getVariantPageRule({
          variantPageRef: pageRef,
        })

      if (!currentRule) {
        throw new Error('Variant rule not found')
      }

      const updatedRule = {
        ...currentRule,
        filter: serializeFilter(...filters),
      }

      await editorSdkProxy.editor.routers.updateVariantPageRule({
        variantPageRef: pageRef,
        updatedRule,
      })

      await editorSdkProxy.editor.routers.refresh()
      editorSdkProxy.document.application.reloadManifest()
    },

    ...getEditor3Api({
      editorSdkProxy,
      finalAppApiPromise,
      collectionsApi,
      i18n,
      fedopsLogger,
      httpClient,
      editorType,
    }),
  }
}

const createDpVariant = async ({
  layout,
  title,
  filters,
  originalPageRef,
  editorSdkProxy,
  appApi,
  editorType,
  pageIdForDuplication,
}: {
  layout: 'blank' | 'duplicate'
  title: string
  filters: any[]
  originalPageRef: PageRef
  editorSdkProxy: EditorSDK
  appApi: AppApi
  editorType: EDITOR_TYPE
  pageIdForDuplication: string
}): Promise<PageRef> => {
  const routerDataset = await fetchRouterDatasetFromPage(
    editorSdkProxy,
    originalPageRef,
  )

  const isAddingBlankVariant = layout === 'blank'

  const variantPageRef = (
    isAddingBlankVariant
      ? await editorSdkProxy.pages.add({
          title,
          definition: { data: { managingAppDefId: 'dataBinding' } },
          shouldAddMenuItem: false,
          shouldNavigateToPage: false,
        })
      : await editorSdkProxy.pages.duplicate({
          pageId: pageIdForDuplication,
          shouldAddMenuItem: false,
          shouldDuplicatePageCode: false,
        })
  ) as PageRef

  const rule = {
    name: `rule-${variantPageRef.id}`,
    filter: serializeFilter(...filters),
  }

  const variantId = `variant-${variantPageRef.id}`

  const data = {
    pageRef: originalPageRef,
    rule,
    variantId,
    variantPageRef,
  }

  await editorSdkProxy.editor.routers.addVariantToPage(data)

  await Promise.all([
    setDynamicPageTypeStateForPage({
      editorSdkProxy,
      pageRef: variantPageRef,
      pageType: DYNAMIC_PAGE_VARIANT,
    }),
    !isAddingBlankVariant &&
      editorSdkProxy.pages.rename({ title, pageRef: variantPageRef }),
    isAddingBlankVariant &&
      addRouterDataset({
        editorSdkProxy,
        appApi,
        editorType,
        pageRef: variantPageRef,
        collectionName: '',
        pageType: PAGE_TYPES.ITEM,
        displayName: routerDataset?.data.displayName,
        readWriteType: routerDataset?.data.config?.dataset?.readWriteType,
      }),
  ])

  await editorSdkProxy.pages.navigateTo({ pageRef: variantPageRef })
  editorSdkProxy.document.application.reloadManifest()
  editorSdkProxy.editor.routers.refresh()

  return variantPageRef
}
